import { Pagination } from "../types/pagination";
import {
  PostSalesInvoiceItem,
  PostSalesInvoiceListResult,
  PostSalesInvoiceSummary,
} from "../types/post-sales-invoice";
import {
  IPostSalesInvoiceService,
  PostSalesInvoiceCreationResult,
} from "../types/services/post-sales-invoice-service";
import { FetchResult, GET, POST } from "../utils/fetch";

class PostSalesInvoiceService implements IPostSalesInvoiceService {
  async getInvoices (
    vertical?: string,
    invoice_type?: string,
    status?: string,
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<PostSalesInvoiceListResult>> {
    const qp = new URLSearchParams();

    if (vertical) {
      qp.set("vertical", vertical);
    }

    if (invoice_type) {
      qp.set("invoice_type", invoice_type);
    }

    if (status) {
      qp.set("status", status);
    }

    if (search) {
      qp.set("query", search);
    }

    if (page) {
      qp.set("page", page);
    }

    if (page_size) {
      qp.set("per_page", page_size);
    }

    const marshalled = qp.toString();

    let url = "/api/v2/post_sales_invoices";

    if (marshalled) {
      url = `${url}?${marshalled}`;
    }

    const { error, response } = await GET<InvoiceListResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown Error"),
        response: null,
      };
    }

    const invoices = response.invoices.map(each => {
      const {
          id,
          number,
          vertical,
          invoice_type,
          status,
          generatable,
          cancellable,
          regeneratable,
          errors,
          cancellation_reason,
          cancellation_remark,
          amount,
          billing_name,
          milestone_name,
          property_name,
          tds_status,
          opportunity_slug,
          updated_at,
        } = each,
        invoice: PostSalesInvoiceItem = {
          id: id ?? -1,
          number: number ?? "",
          vertical: vertical ?? "",
          invoice_type: invoice_type ?? "",
          status: status ?? "",
          generatable: generatable ?? false,
          cancellable: cancellable ?? false,
          regeneratable: regeneratable ?? false,
          errors: errors ?? [],
          cancellation_reason: cancellation_reason ?? "",
          cancellation_remark: cancellation_remark ?? "",
          amount: amount ?? 0,
          billing_name: billing_name ?? "",
          milestone_name: milestone_name ?? "",
          property_name: property_name ?? "",
          tds_status: tds_status ?? "",
          opportunity_slug: opportunity_slug ?? "",
          updated_at: updated_at ?? "",
        };

      return invoice;
    });

    const { summary } = response,
      mappedSummary: PostSalesInvoiceSummary = {
        not_generated: summary?.not_generated ?? 0,
        generated: summary?.generated ?? 0,
        error: summary?.error ?? 0,
        cancelled: summary?.cancelled ?? 0,
      };

    return {
      error: null,
      paginate: response.paginate,
      response: {
        invoices: invoices,
        summary: mappedSummary,
      },
    };
  }

  async getInvoiceSummary (
    vertical?: string,
    invoice_type?: string,
    search?: string,
  ): Promise<FetchResult<PostSalesInvoiceSummary>> {
    const qp = new URLSearchParams();

    if (vertical) {
      qp.set("vertical", vertical);
    }

    if (invoice_type) {
      qp.set("invoice_type", invoice_type);
    }

    if (search) {
      qp.set("query", search);
    }

    // We only need the per-status summary, not the rows.
    qp.set("per_page", "1");

    const url = `/api/v2/post_sales_invoices?${qp.toString()}`;

    const { error, response } = await GET<InvoiceListResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown Error"),
        response: null,
      };
    }

    const { summary } = response,
      mapped: PostSalesInvoiceSummary = {
        not_generated: summary?.not_generated ?? 0,
        generated: summary?.generated ?? 0,
        error: summary?.error ?? 0,
        cancelled: summary?.cancelled ?? 0,
      };

    return {
      error: null,
      response: mapped,
    };
  }

  async generateInvoice (id: number): Promise<FetchResult<void>> {
    const url = `/api/v2/post_sales_invoices/${id}/generate`,
      { error } = await POST<void>(url, "");

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async cancelInvoice (
    id: number,
    reason: string,
    comments: string,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/post_sales_invoices/${id}/cancel`,
      payload = {
        reason: reason,
        remarks: comments,
      },
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async regenerateInvoice (id: number): Promise<FetchResult<void>> {
    const url = `/api/v2/post_sales_invoices/${id}/regenerate`,
      { error } = await POST<void>(url, "");

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async refreshInvoice (id: number): Promise<FetchResult<void>> {
    const url = `/api/v2/post_sales_invoices/${id}/refresh`,
      { error } = await POST<void>(url, "");

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }

  async getInvoiceErrors (id: number): Promise<FetchResult<string[]>> {
    const url = `/api/v2/post_sales_invoices/${id}/errors`,
      { error, response } = await GET<InvoiceErrorsResponse>(url);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: response?.errors ?? [],
    };
  }

  async createTaxInvoices (
    types: string[],
    month: string,
  ): Promise<FetchResult<PostSalesInvoiceCreationResult>> {
    const url = "/api/v2/post_sales_invoices",
      payload = { types, month },
      { error, response } = await POST<PostSalesInvoiceCreationResult>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    if (!response) {
      return {
        error: new Error("Unknown Error"),
        response: null,
      };
    }

    return {
      error: null,
      response,
    };
  }

  async generateProforma (
    vertical: string,
    delivery_timeline_id: number,
  ): Promise<FetchResult<void>> {
    const url = "/api/v2/post_sales_invoices/generate_proforma",
      payload = {
        vertical: vertical,
        delivery_timeline_id: delivery_timeline_id,
      },
      { error } = await POST<void>(url, payload);

    if (error) {
      return {
        error,
        response: null,
      };
    }

    return {
      error: null,
      response: null,
    };
  }
}

export default PostSalesInvoiceService;

type InvoiceListResponse = {
  invoices: any[];
  paginate: Pagination;
  summary?: PostSalesInvoiceSummary;
};

type InvoiceErrorsResponse = {
  errors: string[];
};
